import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Coins, Fuel, Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Select } from '../components/ui/Input';
import { PageLoader } from '../components/ui/Spinner';
import { useExpenseItems, useExpenseSummary } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { locale, t, tr } from '../i18n';
import type { ExpenseCar, ExpenseCategory, ExpenseItem, ExpenseSummary } from '../types';
import { cn, countryName, formatCurrency, formatDate, formatNumber } from '../utils/helpers';

const THIS_YEAR = new Date().getFullYear();
/** Short month names in the current language ("jan", "Jan", "Jan.") */
const months = () => Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(locale(), { month: 'short' }).replace(/\.$/, ''));

const CATEGORY_LABELS: Record<ExpenseCategory, string> = tr({
  maintenance: 'Údržba',
  repair: 'Opravy',
  inspection: 'Kontrola / STK / EK',
  tires: 'Pneumatiky',
  other: 'Ostatné',
  vignette: 'Diaľničné známky',
  fine: 'Pokuty',
  fuel: 'Palivo',
  fuel_other: 'AdBlue a nákupy na pumpe',
});

const CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  maintenance: 'bg-sky-500',
  repair: 'bg-red-500',
  inspection: 'bg-violet-500',
  tires: 'bg-slate-500',
  other: 'bg-secondary-400',
  vignette: 'bg-teal-500',
  fine: 'bg-amber-500',
  fuel: 'bg-orange-600',
  fuel_other: 'bg-lime-600',
};

const CATEGORIES = Object.keys(CATEGORY_COLORS) as ExpenseCategory[];

/** Where the cost was entered */
const sourceLink = (item: ExpenseItem) =>
  item.source === 'service'
    ? `/cars/${item.car_id}?tab=services`
    : item.source === 'vignette'
      ? '/vignettes'
      : item.source === 'fuel'
        ? `/fuel?tab=transactions&car_id=${item.car_id}`
        : '/fines';

const formatConsumption = (value: number | null) => (value === null ? '–' : `${formatNumber(value, 1)} l/100 km`);

/** Costs paid for the car's upkeep (everything except repairs, vignettes and fines) */
const upkeep = (c: ExpenseCar) => c.by_category.maintenance + c.by_category.inspection + c.by_category.tires + c.by_category.other;

export function Expenses() {
  const [filters, setFilters] = useUrlFilters({ year: String(THIS_YEAR), car: '' });
  const year = toInt(filters.year);
  const carId = toInt(filters.car);
  const { data: summary, isLoading } = useExpenseSummary(year);
  const car = summary?.cars.find((c) => c.car_id === carId);

  return (
    <>
      <PageHeader
        title={t('Náklady')}
        subtitle={t('Celkové náklady vozového parku a náklady jednotlivých vozidiel')}
        actions={
          <Select
            aria-label={t('Rok')}
            wrapperClassName="w-36"
            placeholder={t('Všetky roky')}
            options={(summary?.years ?? [THIS_YEAR]).map((y) => ({ value: String(y), label: String(y) }))}
            value={filters.year}
            onChange={(e) => setFilters({ year: e.target.value })}
          />
        }
      />

      <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          {t('Počítajú sa servisy a opravy s cenou, diaľničné známky, pokuty a tankovanie (palivo bez DPH).')}{' '}
          <strong>{t('Pripravujeme:')}</strong> {t('faktúry z doklado.sk.')}
        </p>
      </div>

      {isLoading || !summary ? (
        <PageLoader />
      ) : car ? (
        <CarExpenses car={car} summary={summary} year={year} onBack={() => setFilters({ car: '' })} />
      ) : (
        <Overview summary={summary} year={year} onSelectCar={(id) => setFilters({ car: String(id) })} />
      )}
    </>
  );
}

function Tile({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <Card padding="sm">
      <p className="text-sm text-secondary-500">{label}</p>
      <p className="mt-1 truncate text-2xl font-bold tabular-nums text-secondary-900">{value}</p>
      {note && <p className="mt-0.5 truncate text-xs text-secondary-500">{note}</p>}
    </Card>
  );
}

function Overview({ summary, year, onSelectCar }: { summary: ExpenseSummary; year?: number; onSelectCar: (id: number) => void }) {
  const cars = summary.cars;
  const withCosts = cars.filter((c) => c.total > 0);
  const mostRepairs = [...cars].sort((a, b) => b.repair_count - a.repair_count || b.by_category.repair - a.by_category.repair)[0];
  const maxTotal = Math.max(...cars.map((c) => c.total), 1);
  const maxRepairs = Math.max(...cars.map((c) => c.repair_count));
  const perKm = cars.map((c) => c.cost_per_km).filter((v): v is number => v !== null);
  const maxPerKm = perKm.length > 1 ? Math.max(...perKm) : null;

  const columns: Column<ExpenseCar>[] = [
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (c) => (
        <div>
          <p className="font-medium text-secondary-900">{c.license_plate}</p>
          <p className="text-xs text-secondary-500">
            {c.brand} {c.model}
          </p>
        </div>
      ),
    },
    {
      key: 'total',
      header: t('Spolu'),
      render: (c) => (
        <div className="min-w-[8rem]">
          <p className="font-semibold tabular-nums">{formatCurrency(c.total)}</p>
          <div className="mt-1 h-1.5 rounded-full bg-secondary-100">
            <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${(c.total / maxTotal) * 100}%` }} />
          </div>
        </div>
      ),
    },
    {
      key: 'repairs',
      header: t('Opravy'),
      render: (c) => (
        <div className="tabular-nums">
          {c.repair_count > 0 && c.repair_count === maxRepairs ? (
            <Badge variant="danger" title={t('Najviac opráv')}>{t('{count}×', { count: c.repair_count })}</Badge>
          ) : (
            <span>{t('{count}×', { count: c.repair_count })}</span>
          )}
          <p className="text-xs text-secondary-500">{formatCurrency(c.by_category.repair)}</p>
        </div>
      ),
    },
    { key: 'upkeep', header: t('Údržba, STK, pneu'), className: 'tabular-nums', render: (c) => formatCurrency(upkeep(c)) },
    { key: 'vignettes', header: t('Známky'), className: 'tabular-nums', render: (c) => formatCurrency(c.by_category.vignette) },
    { key: 'fines', header: t('Pokuty'), className: 'tabular-nums', render: (c) => formatCurrency(c.by_category.fine) },
    { key: 'km', header: t('Najazdené km'), className: 'tabular-nums', render: (c) => (c.km === null ? '–' : formatNumber(c.km)) },
    {
      key: 'per_km',
      header: t('€ / km'),
      className: 'tabular-nums',
      render: (c) =>
        c.cost_per_km === null ? (
          '–'
        ) : c.cost_per_km === maxPerKm ? (
          <Badge variant="warning" title={t('Najvyšší náklad na km')}>{formatNumber(c.cost_per_km, 2)}</Badge>
        ) : (
          formatNumber(c.cost_per_km, 2)
        ),
    },
    {
      key: 'fuel',
      header: t('Palivo'),
      render: (c) => (
        <div className="whitespace-nowrap tabular-nums">
          <p>{formatCurrency(c.by_category.fuel)}</p>
          <p className="text-xs text-secondary-500">{formatConsumption(c.consumption)}</p>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile
          label={year ? t('Náklady spolu {year}', { year }) : t('Náklady spolu')}
          value={formatCurrency(summary.total)}
          note={t('{count} položiek', { count: formatNumber(summary.count) })}
        />
        <Tile label={t('Opravy')} value={formatCurrency(summary.by_category.repair)} note={t('{count} opráv', { count: cars.reduce((n, c) => n + c.repair_count, 0) })} />
        <Tile
          label={t('Priemer na vozidlo')}
          value={formatCurrency(withCosts.length ? summary.total / withCosts.length : 0)}
          note={t('{count} vozidiel s nákladmi', { count: withCosts.length })}
        />
        <Tile
          label={t('Najviac opráv')}
          value={mostRepairs?.repair_count ? mostRepairs.license_plate : '–'}
          note={mostRepairs?.repair_count ? t('{count} opráv, {amount}', { count: mostRepairs.repair_count, amount: formatCurrency(mostRepairs.by_category.repair) }) : t('žiadne opravy')}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {year && (
          <Card>
            <CardHeader title={t('Náklady podľa mesiacov {year}', { year })} />
            <MonthBars year={year} values={summary.by_month} />
          </Card>
        )}
        <Card className={cn(!year && 'lg:col-span-2')}>
          <CardHeader title={t('Podľa kategórií')} />
          <CategoryBars byCategory={summary.by_category} />
        </Card>
      </div>

      <Card padding="none" className="mt-6">
        <div className="p-5 pb-0">
          <CardHeader title={t('Porovnanie vozidiel')} subtitle={t('Kliknite na vozidlo pre detail jeho nákladov')} />
        </div>
        <DataTable
          data={cars}
          columns={columns}
          rowKey={(c) => c.car_id}
          onRowClick={(c) => onSelectCar(c.car_id)}
          emptyMessage={<EmptyState icon={<Coins className="h-10 w-10" />} title={t('Žiadne vozidlá')} />}
        />
      </Card>
    </>
  );
}

function CarExpenses({ car, summary, year, onBack }: { car: ExpenseCar; summary: ExpenseSummary; year?: number; onBack: () => void }) {
  const { data: items, isLoading } = useExpenseItems({ year, car_id: car.car_id });
  const fleetPerKm = (() => {
    const withKm = summary.cars.filter((c) => c.km);
    const km = withKm.reduce((sum, c) => sum + (c.km ?? 0), 0);
    return km ? withKm.reduce((sum, c) => sum + c.total, 0) / km : null;
  })();
  const fleetAverage = (() => {
    const withCosts = summary.cars.filter((c) => c.total > 0);
    return withCosts.length ? summary.total / withCosts.length : 0;
  })();

  const columns: Column<ExpenseItem>[] = [
    { key: 'date', header: t('Dátum'), className: 'whitespace-nowrap', render: (i) => formatDate(i.expense_date) },
    {
      key: 'category',
      header: t('Kategória'),
      render: (i) => (
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span className={cn('h-2.5 w-2.5 rounded-full', CATEGORY_COLORS[i.category])} aria-hidden="true" />
          {CATEGORY_LABELS[i.category]}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('Popis'),
      render: (i) => (
        <Link to={sourceLink(i)} className="text-primary-600 hover:underline" onClick={(e) => e.stopPropagation()}>
          {[i.country && countryName(i.country), i.description].filter(Boolean).join(' · ') || t('Detail')}
        </Link>
      ),
    },
    { key: 'amount', header: t('Suma'), className: 'text-right tabular-nums font-medium', headerClassName: 'text-right', render: (i) => formatCurrency(i.amount) },
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> {t('Všetky vozidlá')}
          </Button>
          <h2 className="text-xl font-bold text-secondary-900">
            {car.license_plate}{' '}
            <span className="font-normal text-secondary-500">
              {car.brand} {car.model}
            </span>
          </h2>
        </div>
        <Link to={`/cars/${car.car_id}`} className="text-sm text-primary-600 hover:underline">
          {t('Karta vozidla')}
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile
          label={year ? t('Náklady spolu {year}', { year }) : t('Náklady spolu')}
          value={formatCurrency(car.total)}
          note={t('priemer vozidla vo vozovom parku {amount}', { amount: formatCurrency(fleetAverage) })}
        />
        <Tile label={t('Opravy')} value={t('{count}×', { count: car.repair_count })} note={formatCurrency(car.by_category.repair)} />
        <Tile label={t('Najazdené km')} value={car.km === null ? '–' : formatNumber(car.km)} note={t('podľa GPS kníh jázd')} />
        <Tile
          label={t('Náklad na km')}
          value={car.cost_per_km === null ? '–' : `${formatNumber(car.cost_per_km, 2)} €`}
          note={fleetPerKm !== null ? t('priemer vozového parku {value} €', { value: formatNumber(fleetPerKm, 2) }) : undefined}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {year && (
          <Card className="lg:col-span-2">
            <CardHeader title={t('Náklady podľa mesiacov {year}', { year })} />
            <MonthBars year={year} values={car.by_month} />
          </Card>
        )}
        <Card className={cn(!year && 'lg:col-span-2')}>
          <CardHeader title={t('Podľa kategórií')} />
          <CategoryBars byCategory={car.by_category} />
        </Card>
        <Card className={cn(year && 'lg:col-span-3')}>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Fuel className="h-4 w-4 text-secondary-500" /> {t('Palivo a spotreba')}
              </span>
            }
            action={
              <Link to={`/fuel?tab=transactions&car_id=${car.car_id}`} className="text-sm text-primary-600 hover:underline">
                {t('Tankovania vozidla')}
              </Link>
            }
          />
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <FuelFact label={t('Palivo bez DPH')} value={formatCurrency(car.by_category.fuel)} />
            <FuelFact label={t('Natankované')} value={car.fuel_liters === null ? '–' : `${formatNumber(car.fuel_liters, 1)} l`} />
            <FuelFact label={t('Priemerná spotreba')} value={formatConsumption(car.consumption)} />
            <FuelFact label={t('AdBlue a nákupy na pumpe')} value={formatCurrency(car.by_category.fuel_other)} />
          </dl>
          {car.consumption === null && car.fuel_liters !== null && (
            <p className="mt-3 text-xs text-secondary-500">{t('Spotreba sa vypočíta pre mesiace, v ktorých sú tankovania aj GPS kilometre.')}</p>
          )}
        </Card>
      </div>

      <Card padding="none" className="mt-6">
        <div className="p-5 pb-0">
          <CardHeader title={t('Položky nákladov')} />
        </div>
        <DataTable
          data={items}
          columns={columns}
          rowKey={(i) => `${i.source}-${i.source_id}`}
          isLoading={isLoading}
          emptyMessage={<EmptyState icon={<Coins className="h-10 w-10" />} title={t('Žiadne náklady v tomto období')} />}
        />
      </Card>
    </>
  );
}

function FuelFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-secondary-500">{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums text-secondary-900">{value}</dd>
    </div>
  );
}

function MonthBars({ year, values }: { year: number; values: number[] }) {
  const MONTHS = months();
  const max = Math.max(...values, 1);
  const peak = values.indexOf(max);
  return (
    <div>
      <div className="flex h-40 items-end gap-1.5 border-b border-secondary-300 sm:gap-3">
        {values.map((v, i) => (
          <div key={MONTHS[i]} className="relative flex h-full flex-1 items-end" title={`${MONTHS[i]} ${year}: ${formatCurrency(v)}`}>
            {i === peak && v > 0 && (
              <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-secondary-700">{formatCurrency(v)}</span>
            )}
            <div className="w-full rounded-t bg-primary-600 transition-opacity hover:opacity-80" style={{ height: v ? `${Math.max((v / max) * 85, 2)}%` : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5 text-center text-xs text-secondary-500 sm:gap-3">
        {MONTHS.map((m) => (
          <span key={m} className="flex-1">
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

function CategoryBars({ byCategory }: { byCategory: Record<ExpenseCategory, number> }) {
  const total = CATEGORIES.reduce((sum, c) => sum + byCategory[c], 0);
  if (!total) return <EmptyState icon={<Coins className="h-10 w-10" />} title={t('Žiadne náklady v tomto období')} />;
  const rows = CATEGORIES.filter((c) => byCategory[c] > 0).sort((a, b) => byCategory[b] - byCategory[a]);
  return (
    <ul className="space-y-3">
      {rows.map((c) => (
        <li key={c}>
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-secondary-700">{CATEGORY_LABELS[c]}</span>
            <span className="tabular-nums text-secondary-900">
              {formatCurrency(byCategory[c])} <span className="text-secondary-400">({Math.round((byCategory[c] / total) * 100)} %)</span>
            </span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-secondary-100">
            <div className={cn('h-2 rounded-full', CATEGORY_COLORS[c])} style={{ width: `${(byCategory[c] / total) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
